<template>
    <div :class="inputClass" v-bind="$attrs" class="zs-dropdown-container" v-click-outside="closeDropdown" @keydown.esc="closeDropdown"
        @keydown.down.prevent="handleDownKey" @keydown.up.prevent="handleUpKey" @keydown.enter.prevent="handleEnterKey">
        <div class="zs-dropdown-toggle">
            <div class="zs-multiselect-input-wrapper" @click="handleInputWrapperClick">
                <ZSInput v-bind="$attrs" :disabled="disabled" :type="showDropdownIcon?'dropdown':''" @drop="toggleDropdown" :clear="clear" @clear="handleClear" :busy="isBusy"
                    :label="label" v-model="internalValue" :valid="true" :toValidate="false" @input="onInput" @click="handleInputClick"
                    :placeholder="selectedItems.length > 0 ? '' : placeholder" :error="validationResult.description" :size="size" ref="zsInput">
                </ZSInput>
                <div v-if="selectedItems.length > 0" :class="{'zs-multiselect-chips-overlay':true,[`size_${size}`]:true}" @click="handleChipsClick">
                    <ZSChipInput
                        :chips="selectedItemLabels"
                        @update:chips="onChipRemove"
                        :editable="false"
                        :show-border="false"
                        :chip-delete="true"
                        :size="size"
                        :wrap="false"
                        chip-color="#EB7629"
                        chip-text-color="white"
                        class="zs-multiselect-chips"
                    />
                </div>
            </div>
        </div>
        <div v-if="isOpen" ref="zsMenu" :class="{ 'zs-menu-top': isAbove, 'no-label': !label }" class="zs-dropdown-menu"
            :style="{ maxHeight: maxHeight }">
            <div v-if="showSelectAll" class="zs-dropdown-item" @click="toggleSelectAll">
                <input type="checkbox" :checked="isAllSelected" />
                <span>{{ isAllSelected ? t('components.deselect_all') : t('components.select_all') }}</span>
            </div>
            <div v-for="(item, index) in internalItems" :key="index"
                :class="['zs-dropdown-item', { 'active': index === activeItemIndex }, size]" @click="selectItem(item)">
                <input type="checkbox" :checked="isSelected(item)" />
                <slot :item="item">
                    <span style="font-size: 80%;">{{ item[itemLabel] ||  item }}</span>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ZSInput from './ZSInput.vue';
import ZSChipInput from './ZSChipInput.vue';

const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select items' },
    maxHeight: { type: String, default: '200px' },
    label: { type: String, default: '' },
    rules: { type: String, default: '' },
    itemLabel: { type: String, default: 'label' },
    itemValue: { type: String, default: null },
    busy: { type: Boolean, default: false },
    loadFunction: { type: Function, default: null },
    delay: { type: Number, default: 300 },
    clear: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: 'small' },
    inputClass: { type: String, default: '' },
    showDropdownIcon: { type: Boolean, default: true },
    showSelectAll: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const validator = inject('validator', null);
const zsInput = ref(null);
const zsMenu = ref(null);
const isOpen = ref(false);
const isBusy = ref(props.busy);
const isAbove = ref(false);
const validationResult = ref({});
const internalItems = ref([...props.items]);
const selectedItems = ref([]);
const activeItemIndex = ref(-1);

const internalValue = computed({
    get: () => {
        if (selectedItems.value.length === 0) {
            return '';
        }
        return ' '; // Return a space to make the label float when items are selected
    },
    set: (val) => {
        // Allow the input to be cleared or typed in for filtering
        if (!val || val.trim() === '') {
            // Reset to show all items when input is cleared
            internalItems.value = [...props.items];
        }
    }
});

const selectedItemLabels = computed(() => {
    return selectedItems.value.map(item => item[props.itemLabel] || item);
});

function onChipRemove(remainingLabels) {
    // Find which items should remain based on the remaining labels
    const newSelectedItems = [];
    const usedLabels = new Set();

    for (const label of remainingLabels) {
        // Find the first unused item that matches this label
        const matchingItem = selectedItems.value.find(item => {
            const itemLabel = item[props.itemLabel] || item;
            const itemKey = props.itemValue ? item[props.itemValue] : item;
            return itemLabel === label && !usedLabels.has(itemKey);
        });

        if (matchingItem) {
            newSelectedItems.push(matchingItem);
            const itemKey = props.itemValue ? matchingItem[props.itemValue] : matchingItem;
            usedLabels.add(itemKey);
        }
    }

    selectedItems.value = newSelectedItems;
    const newModelValue = selectedItems.value.map(i => props.itemValue ? i[props.itemValue] : i);
    emit('update:modelValue', newModelValue);
    validate();
}


function getInitialSelectedItems() {
    if (!Array.isArray(props.modelValue)) return [];
    return props.items.filter(item => {
        const val = props.itemValue ? item[props.itemValue] : item;
        return props.modelValue.includes(val);
    });
}

selectedItems.value = getInitialSelectedItems();

watch(() => props.modelValue, (newValue) => {
    if (JSON.stringify(newValue) !== JSON.stringify(selectedItems.value.map(i => props.itemValue ? i[props.itemValue] : i))) {
        selectedItems.value = props.items.filter(item => {
            const val = props.itemValue ? item[props.itemValue] : item;
            return newValue.includes(val);
        });
    }
}, { deep: true });

watch(() => props.items, (newItems) => {
    internalItems.value = [...newItems];
    // Re-evaluate selectedItems based on current modelValue and new items
    selectedItems.value = getInitialSelectedItems();
}, { deep: true });

function toggleDropdown() {
    if (props.disabled) return;
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        nextTick(positionMenu);
    }
}

function handleInputWrapperClick(event) {
    // Check if the click was on the clear icon or its children
    const clickedElement = event.target;
    const isClearClick = clickedElement.closest('.reset') ||
                        clickedElement.classList.contains('reset') ||
                        (clickedElement.tagName === 'svg' && clickedElement.parentElement?.classList.contains('reset')) ||
                        (clickedElement.tagName === 'path' && clickedElement.closest('.reset'));

    // Don't toggle dropdown if clicking on clear icon
    if (isClearClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }

    // Toggle dropdown when clicking anywhere else on the wrapper
    if (!props.disabled) {
        toggleDropdown();
    }
}

function handleInputClick(event) {
    // Check if the click was on the clear icon or its children
    const clickedElement = event.target;
    const isClearClick = clickedElement.closest('.reset') ||
                        clickedElement.classList.contains('reset') ||
                        (clickedElement.tagName === 'svg' && clickedElement.parentElement?.classList.contains('reset')) ||
                        (clickedElement.tagName === 'path' && clickedElement.closest('.reset'));

    // Don't toggle dropdown if clicking on clear icon
    if (isClearClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }

    // When clicking directly on the input, toggle dropdown and allow focusing for typing
    if (!props.disabled) {
        const wasOpen = isOpen.value;
        toggleDropdown();
        // Allow the input to be focused for typing/filtering when dropdown opens
        if (!wasOpen && isOpen.value) {
            nextTick(() => {
                event.target.focus();
            });
        }
    }
}

function handleClear(event) {
    // Prevent the clear action from bubbling and triggering dropdown
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    // Close dropdown when clearing
    isOpen.value = false;
    // Reset the selection
    reset();
}

function handleChipsClick(event) {
    // When clicking on the chips area (but not on a chip remove button or clear icon), toggle dropdown
    if (!props.disabled) {
        // Check if the click was on a chip remove button
        const isRemoveButton = event.target.closest('.chip-remove-button') ||
                               event.target.closest('.zs-chip-remove') ||
                               event.target.classList.contains('chip-remove-icon');

        // Check if the click was on the clear icon
        const clickedElement = event.target;
        const isClearClick = clickedElement.closest('.reset') ||
                            clickedElement.classList.contains('reset') ||
                            (clickedElement.tagName === 'svg' && clickedElement.parentElement?.classList.contains('reset')) ||
                            (clickedElement.tagName === 'path' && clickedElement.closest('.reset'));

        if (!isRemoveButton && !isClearClick) {
            // Prevent event from bubbling to parent handlers
            event.stopPropagation();
            toggleDropdown();
        }
    }
}

function closeDropdown() {
    isOpen.value = false;
    activeItemIndex.value = -1;
}

function selectItem(item) {
    const index = selectedItems.value.findIndex(i => (props.itemValue ? i[props.itemValue] : i) === (props.itemValue ? item[props.itemValue] : item));
    if (index > -1) {
        selectedItems.value.splice(index, 1);
    } else {
        selectedItems.value.push(item);
    }
    const newModelValue = selectedItems.value.map(i => props.itemValue ? i[props.itemValue] : i);
    emit('update:modelValue', newModelValue);
    validate();
}

function isSelected(item) {
    return selectedItems.value.some(i => (props.itemValue ? i[props.itemValue] : i) === (props.itemValue ? item[props.itemValue] : item));
}

const isAllSelected = computed(() => {
    return internalItems.value.length > 0 && selectedItems.value.length === internalItems.value.length;
});

function toggleSelectAll() {
    if (isAllSelected.value) {
        selectedItems.value = [];
    } else {
        selectedItems.value = [...internalItems.value];
    }
    const newModelValue = selectedItems.value.map(i => props.itemValue ? i[props.itemValue] : i);
    emit('update:modelValue', newModelValue);
}


const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
};

const loadFn = props.loadFunction ? debounce(loadItems, props.delay) : null;

function onInput(val) {
    // When typing, clear the transparent text and allow normal input
    if (val && val.trim().length > 0) {
        isOpen.value = true;
        if (loadFn) {
            internalItems.value = [];
            if ((val?.trim())?.length) {
                isBusy.value = true;
                loadFn(val);
            }
        } else {
            internalItems.value = props.items.filter((item) =>
                (item[props.itemLabel] || item).toLowerCase().includes(val.toLowerCase())
            );
        }
    } else {
        internalItems.value = [...props.items];
    }
}

function loadItems(search) {
    props.loadFunction(search).then((items) => {
        internalItems.value = items;
        isBusy.value = false;
    });
}

function reset() {
    selectedItems.value = [];
    validationResult.value = {};
    emit('update:modelValue', []);
}

function validate() {
    if (validator && props.rules) {
        validationResult.value = validator.validate({
            value: selectedItems.value,
            rules: props.rules
        });
    }
}

function positionMenu() {
    if (!isOpen.value || !zsInput.value || !zsMenu.value) return;
    const inputRect = zsInput.value.getRef().getBoundingClientRect();
    const menuRect = zsMenu.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    isAbove.value = inputRect.bottom + menuRect.height > viewportHeight && inputRect.top - menuRect.height > 0;
}

function handleDownKey() {
    if (!isOpen.value) {
        toggleDropdown();
    } else if (internalItems.value.length > 0) {
        activeItemIndex.value = (activeItemIndex.value + 1) % internalItems.value.length;
        scrollToActiveItem();
    }
}

function handleUpKey() {
    if (isOpen.value && internalItems.value.length > 0) {
        activeItemIndex.value = (activeItemIndex.value - 1 + internalItems.value.length) % internalItems.value.length;
        scrollToActiveItem();
    }
}

function handleEnterKey() {
    if (isOpen.value && activeItemIndex.value >= 0) {
        selectItem(internalItems.value[activeItemIndex.value]);
    } else {
       toggleDropdown();
    }
}

function scrollToActiveItem() {
    nextTick(() => {
        const activeItemEl = zsMenu.value?.querySelector('.zs-dropdown-item.active');
        if (activeItemEl) {
            activeItemEl.scrollIntoView({ block: 'nearest' });
        }
    });
}

onMounted(() => {
    if (validator) {
        validator.subscribe({ validate, reset });
        validate();
    }
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', positionMenu);
    window.removeEventListener('scroll', positionMenu, true);
});

</script>

<style scoped>
.zs-dropdown-container {
    position: relative;
}
.zs-dropdown-toggle {
    cursor: pointer;
}
.zs-multiselect-input-wrapper {
    position: relative;
    cursor: pointer;
}

.zs-multiselect-chips-overlay {
    position: absolute;
    left: 12px;
    right: 30px; /* Leave space for dropdown icon */
    height: 32px; /* Match small input height */
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    pointer-events: auto; /* Allow clicks to be handled */
    z-index: 1;
    cursor: pointer;
    /*overflow: hidden;*/
    &.size_medium {
        left: 15px;
        height: 40px; /* Match medium input height */
    }
    &.size_big {
        left: 15px;
        height: 50px; /* Match big input height */
    }
}

.zs-multiselect-chips {
    pointer-events: auto;
    cursor: pointer;
    width: 100%;
}

.zs-multiselect-chips-overlay :deep(.chip-input) {
    margin: 0 !important;
    padding: 0 !important;
}

.zs-multiselect-chips-overlay :deep(.chips-holder) {
    justify-content: flex-start;
}

/* .zs-multiselect-chips :deep(.chip-input) {
    margin: 0;
    padding: 0 12px;
    border: none;
    background: transparent;
    cursor: pointer;
}

.zs-multiselect-chips :deep(.chips-holder) {
    gap: 4px;
}

.zs-multiselect-chips :deep(.chip) {
    font-size: 0.75em;
    padding: 2px 6px 2px 8px;
    border-radius: 12px;
    cursor: default;
}

.zs-multiselect-chips :deep(.chip .chip-remove-button),
.zs-multiselect-chips :deep(.chip .zs-chip-remove) {
    cursor: pointer;
}

.zs-multiselect-chips :deep(.chip.size-small) {
    font-size: 0.7em;
    padding: 1px 4px 1px 6px;
}

.zs-multiselect-input-wrapper :deep(.zs-input) {
    cursor: pointer;
}

.zs-multiselect-input-wrapper :deep(.zs-input input) {
    color: transparent;
    cursor: pointer;
} */

.zs-multiselect-input-wrapper :deep(.zs-input input):focus {
    color: var(--zs-input-color, #21425F);
    cursor: text;
}

.zs-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: white;
    border: 1px solid #ccc;
    overflow-y: auto;
    z-index: 1000;
    border-top: none;
    border-radius: 0 0 4px 4px;
}

.zs-dropdown-menu.zs-menu-top {
    top: auto;
    bottom: 100%;
    border-radius: 4px 4px 0 0;
}

.zs-dropdown-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    border-top: 1px solid #eee;
}

.zs-dropdown-item.small {
    padding: 4px 8px;
    font-size: 0.875em;
}

.zs-dropdown-item.large {
    padding: 12px 16px;
    font-size: 1.125em;
}

.zs-dropdown-item input[type="checkbox"] {
    margin-right: 8px;
}

.zs-dropdown-item:hover, .zs-dropdown-item.active {
    background-color: #f0f0f0;
}

</style>
